import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import UserLayout from '@/components/Layout/UserLayout';
import { Badge, Button, Card, FormInput } from '@/components/UI';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner';
import { useTradeLogicDefinitions, type TradeLogicDefinitionSummary } from '@/features/tradeLogicAuthoring/api';

type KindFilter = 'all' | 'entry' | 'exit';

const formatDate = (iso: string | null): string => {
    if (!iso) return '—';

    try {
        return new Date(iso).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
        return iso;
    }
};

const TradeLogicLibraryPage = () => {
    const navigate = useNavigate();
    const [search, setSearch] = useState('');
    const [kindFilter, setKindFilter] = useState<KindFilter>('all');

    const entryDefinitionsQuery = useTradeLogicDefinitions({ enabled: true, isActiveOnly: true, kind: 'entry' });
    const exitDefinitionsQuery = useTradeLogicDefinitions({ enabled: true, isActiveOnly: true, kind: 'exit' });

    const isLoading = entryDefinitionsQuery.isLoading || exitDefinitionsQuery.isLoading;

    const allDefinitions = useMemo<TradeLogicDefinitionSummary[]>(() => {
        const combined = [...entryDefinitionsQuery.definitions, ...exitDefinitionsQuery.definitions];
        return combined.sort((left, right) => {
            const leftStamp = left.updatedAt ?? left.createdAt;
            const rightStamp = right.updatedAt ?? right.createdAt;
            return rightStamp.localeCompare(leftStamp);
        });
    }, [entryDefinitionsQuery.definitions, exitDefinitionsQuery.definitions]);

    const filteredDefinitions = useMemo(() => {
        const query = search.trim().toLowerCase();
        return allDefinitions.filter((definition) => {
            if (kindFilter !== 'all' && definition.kind !== kindFilter) return false;
            if (!query) return true;
            return definition.name.toLowerCase().includes(query) || (definition.description?.toLowerCase().includes(query) ?? false);
        });
    }, [allDefinitions, kindFilter, search]);

    return (
        <UserLayout containerSize="1200px" title="Trading Rules — Project 314">
            <div className="mb-6">
                <h1 className="text-2xl font-semibold text-zinc-100">Trading Rules</h1>
                <p className="mt-1 text-sm text-zinc-400">
                    Define when your strategies enter and exit trades. Attach saved rule sets to any strategy.
                </p>
            </div>
            <Card className="mb-4">
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div className="flex flex-wrap items-center gap-2">
                        <KindPill
                            active={kindFilter === 'all'}
                            label={`All (${allDefinitions.length})`}
                            onSelect={() => setKindFilter('all')}
                        />
                        <KindPill
                            active={kindFilter === 'entry'}
                            label={`Entry (${entryDefinitionsQuery.definitions.length})`}
                            onSelect={() => setKindFilter('entry')}
                        />
                        <KindPill
                            active={kindFilter === 'exit'}
                            label={`Exit (${exitDefinitionsQuery.definitions.length})`}
                            onSelect={() => setKindFilter('exit')}
                        />
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <FormInput
                            noMargin
                            id="library-search"
                            label=""
                            placeholder="Search rules…"
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                        />
                        <Button size="sm" variant="primary" onClick={() => navigate('/user/tradeLogic/entry/new')}>
                            + New entry rules
                        </Button>
                        <Button size="sm" variant="primary" onClick={() => navigate('/user/tradeLogic/exit/new')}>
                            + New exit rules
                        </Button>
                    </div>
                </div>
            </Card>
            {isLoading ? (
                <Card>
                    <DataLoadingSpinner />
                </Card>
            ) : filteredDefinitions.length === 0 ? (
                <Card>
                    <div className="py-8 text-center">
                        <p className="text-sm text-zinc-400">
                            {search || kindFilter !== 'all' ? 'No rules match your filters.' : 'You haven’t created any rules yet.'}
                        </p>
                        {!search && kindFilter === 'all' && (
                            <div className="mt-4 flex flex-wrap justify-center gap-2">
                                <Button variant="primary" onClick={() => navigate('/user/tradeLogic/entry/new')}>
                                    Create entry rules
                                </Button>
                                <Button variant="primary" onClick={() => navigate('/user/tradeLogic/exit/new')}>
                                    Create exit rules
                                </Button>
                            </div>
                        )}
                    </div>
                </Card>
            ) : (
                <div className="space-y-2">
                    {filteredDefinitions.map((definition) => (
                        <DefinitionRow definition={definition} key={`${definition.kind}-${definition.definitionId}`} />
                    ))}
                </div>
            )}
        </UserLayout>
    );
};

const KindPill = ({ active, label, onSelect }: { active: boolean; label: string; onSelect: () => void }) => (
    <Button size="sm" variant={active ? 'primary' : 'outline-dark'} onClick={onSelect}>
        {label}
    </Button>
);

const DefinitionRow = ({ definition }: { definition: TradeLogicDefinitionSummary }) => {
    const editPath = `/user/tradeLogic/${definition.kind}/${definition.definitionId}`;

    const versionLabel = definition.latestCompiledVersion !== null ? `v${definition.latestCompiledVersion}` : 'no compiled version';

    return (
        <Link className="block" to={editPath}>
            <Card className="transition hover:border-zinc-600">
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                            <Badge variant={definition.kind === 'entry' ? 'success' : 'warning'}>
                                {definition.kind === 'entry' ? 'Entry' : 'Exit'}
                            </Badge>
                            <Badge variant="dark">{versionLabel}</Badge>
                            {!definition.isActive && <Badge variant="secondary">Inactive</Badge>}
                            <span className="text-base font-medium text-zinc-100">{definition.name}</span>
                        </div>
                        {definition.description && <p className="mt-1 text-sm text-zinc-400 line-clamp-2">{definition.description}</p>}
                        <div className="mt-2 text-xs text-zinc-500">
                            #{definition.definitionId} · {definition.scope} · updated{' '}
                            {formatDate(definition.updatedAt ?? definition.createdAt)}
                        </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                        <Button size="sm" variant="secondary">
                            Edit →
                        </Button>
                    </div>
                </div>
            </Card>
        </Link>
    );
};

export default TradeLogicLibraryPage;
